import React, { useMemo } from "react";
import Select from "react-select";
import CustomOption from "./CustomOption";
import { useIsTouchDevice } from "../../../../../../hooks/useIsTouchDevice";
import { useBranding } from "../../../../../../integrations/branding";

const getItemKey = (item: any) => {
    if (item?.value?.id != null) return String(item.value.id);
    if (item?.value?.floorRange != null) return String(item.value.floorRange);
    if (item?.value?.name != null) return String(item.value.name);
    if (item?.name != null) return String(item.name);
    return JSON.stringify(item);
};

export const SelectInstallmentOption = ({
                                            options,
                                            selectedOption,
                                            setSelectedOption,
                                            label,
                                            placeholder,
                                            disabled,
                                            variant,
                                        containerClassName = "",
                                    }: any) => {
    const { isShowcase } = useBranding();
    const isTouch = useIsTouchDevice();
    const isPricingModernVariant = variant === "pricing-modern" && isTouch;
    const isShowcasePricingVariant = variant === "pricing-modern" && isShowcase;
    const placeholderLabel = placeholder ?? label;

    const styles = {
        menu: (base: any) => ({
            ...base,
            background: isShowcasePricingVariant
                ? "var(--brand-surface, #0c1222)"
                : "#fff",
            border: isShowcasePricingVariant
                ? "1px solid var(--brand-border, #22314f)"
                : "1px solid #939baa",
            zIndex: 120,
        }),
        menuPortal: (base: any) => ({
            ...base,
            zIndex: 120,
        }),
        control: (base: any) => ({
            ...base,
            background: isShowcasePricingVariant
                ? "var(--brand-surface-elevated, #121c33)"
                : "#fff",
            border: isPricingModernVariant
                ? isShowcasePricingVariant
                    ? "1px solid var(--brand-border, #22314f)"
                    : "1px solid #e9edf2"
                : "1px solid #939baa",
            borderRadius: isPricingModernVariant ? "16px" : "8px",
            minHeight: isPricingModernVariant ? "84px" : "42px",
            height: isPricingModernVariant ? "84px" : "42px",
            padding: isPricingModernVariant ? "10px 14px 10px" : "0",
            alignItems: isPricingModernVariant ? "flex-start" : "center",
            boxShadow: "none",
            color: isShowcasePricingVariant
                ? "var(--brand-text-primary, #f5f7ff)"
                : "#000",
            opacity: disabled ? 0.6 : 1,
            pointerEvents: disabled ? "none" : "auto",
        }),
        valueContainer: (base: any) => ({
            ...base,
            padding: isPricingModernVariant ? "0" : base.padding,
            height: "100%",
            alignItems: isPricingModernVariant ? "flex-start" : "center",
        }),
        indicatorsContainer: (base: any) => ({
            ...base,
            alignItems: isPricingModernVariant ? "center" : base.alignItems,
            height: "100%",
            paddingTop: isPricingModernVariant ? "18px" : 0,
        }),
        dropdownIndicator: (base: any) => ({
            ...base,
            padding: isPricingModernVariant ? "6px" : base.padding,
            color: isShowcasePricingVariant
                ? "var(--brand-text-secondary, #aab6d3)"
                : base.color,
        }),

        // ✅ выбранное значение / плейсхолдер / ввод — чёрные
        singleValue: (base: any) => ({
            ...base,
            color: isShowcasePricingVariant
                ? "var(--brand-text-primary, #f5f7ff)"
                : "#000",
            margin: isPricingModernVariant ? "28px 0 0" : base.margin,
            fontSize: isPricingModernVariant ? "17px" : base.fontSize,
            fontWeight: isPricingModernVariant ? 500 : base.fontWeight,
            lineHeight: isPricingModernVariant ? "1.2" : base.lineHeight,
        }),
        placeholder: (base: any) => ({
            ...base,
            color: isShowcasePricingVariant
                ? "var(--brand-text-primary, #f5f7ff)"
                : "#000",
            margin: isPricingModernVariant ? "28px 0 0" : base.margin,
            fontSize: isPricingModernVariant ? "17px" : base.fontSize,
            fontWeight: isPricingModernVariant ? 500 : base.fontWeight,
            lineHeight: isPricingModernVariant ? "1.2" : base.lineHeight,
        }),
        input: (base: any) => ({
            ...base,
            color: isShowcasePricingVariant
                ? "var(--brand-text-primary, #f5f7ff)"
                : "#000",
            margin: isPricingModernVariant ? "28px 0 0" : base.margin,
        }),

        option: (base: any, state: any) => ({
            ...base,
            background: isShowcasePricingVariant
                ? state.isSelected
                    ? "var(--brand-surface-alt, #10182c)"
                    : "var(--brand-surface, #0c1222)"
                : state.isSelected
                    ? "#ccc"
                    : "#fff",
            borderBottom: isShowcasePricingVariant
                ? "1px solid var(--brand-border, #22314f)"
                : "1px solid #939baa",
            color: isShowcasePricingVariant
                ? "var(--brand-text-primary, #f5f7ff)"
                : state.isSelected
                    ? "#000"
                    : base.color,
            ":last-child": { border: "none" },
            ":hover": {
                ...base[":hover"],
                background: isShowcasePricingVariant
                    ? "var(--brand-surface-alt, #10182c)"
                    : "#D4D5D5",
            },
            ":active": { ...base[":active"] },
        }),
    };

    const items = useMemo(
        () => Object.values(options || {}).filter(Boolean),
        [options],
    );

    const formattedOptions = useMemo(
        () =>
            items.map((item: any) => ({
                value: item,
                label: item.name,
                _key: getItemKey(item),
            })),
        [items],
    );

    const selectedKey = useMemo(() => {
        if (!selectedOption) return null;
        return getItemKey(selectedOption);
    }, [selectedOption]);

    const selectValue = useMemo(() => {
        if (!selectedKey) return null;
        return formattedOptions.find((o) => o._key === selectedKey) || null;
    }, [formattedOptions, selectedKey]);

    return (
        <div className={`relative z-[20] ${containerClassName}`.trim()}>
            {/* ✅ лейбл чёрный */}
            <div
                className={`${isShowcasePricingVariant ? "pricing-showcase_select-label" : "text-black"} ${isPricingModernVariant ? "pointer-events-none absolute left-[14px] top-[10px] z-[1] mb-0 max-w-[calc(100%-56px)] text-[11px] leading-[1.15] text-[#8a94a3]" : "mb-1 text-[14px]"}`.trim()}
            >
                {label}
            </div>

            <div className={isPricingModernVariant ? "" : "my-2"}>
                <Select
                    value={selectValue}
                    onChange={(opt: any) => opt?.value && setSelectedOption(opt.value)}
                    options={formattedOptions}
                    styles={{
                        ...styles,
                        menuPortal: (base: any) => ({ ...base, zIndex: 120 }),
                        menu: (base: any) => ({ ...base, zIndex: 120 }),
                    }}
                    placeholder={placeholderLabel}
                    components={{ Option: CustomOption }}
                    isDisabled={!!disabled}
                    isSearchable={false}
                    getOptionValue={(opt: any) => opt._key}

                    // ✅ НЕ заставляем вверх
                    menuPlacement="auto"
                    menuShouldScrollIntoView={false}

                    // ✅ на touch порталим в body, чтобы не пряталось под таблицу
                    menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}

                    // ✅ на touch — absolute (как и хотели), на десктопе можно fixed
                    menuPosition={isTouch ? "absolute" : "fixed"}
                />
            </div>
        </div>
    );
};
